import React, { useEffect, useState } from "react"
import { View } from "react-native"
import LoanProductCard from "@/screens/loans/components/LoanProductCard"
import { type LoanApplicationType, type ProductType } from "@/generated/graphql"
import { capitalProductsListScrolled } from "@/analytics/events/loans"
import BFText, { Body } from "@/components/standard/BFText"
import { cn } from "@/lib/utils"
import {
  activeProducts,
  type CombinedProductType,
  mergeLoanProducts,
  type CapitalProductSlug,
  getCapitalProductNameBySlug,
} from "@/screens/loans/utils/LoanCapitalProducts"
import { Tabs, TabsList, TabsTrigger } from "@/components/reusables/ui/tabs"
import LoanProductCardCombined from "@/screens/loans/components/LoanProductCardCombined"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useUser } from "@/queries/user"

const LoanProductScroller = () => {
  const {
    loanId,
    loanApplication,
    capitalProducts,
    isLoading,
    isError,
    userHasRewards,
  } = useLoanContext()
  const { data: userData } = useUser()
  const [mergedLoanProducts, setMergedLoanProducts] = useState<
    ProductType[] | null
  >(null)
  const [currentProduct, setCurrentProduct] = useState<string | null>(null)

  useEffect(() => {
    if (!capitalProducts) {
      return
    }

    // Determine which cards to use based on user rewards
    const productsToUse = activeProducts(
      mergeLoanProducts(capitalProducts as ProductType[])
    )
    setMergedLoanProducts(productsToUse)

    // Synchronize the currentProduct based on mergedLoanProducts
    if (productsToUse.length > 0) {
      const selectedProduct = productsToUse.find(
        (product) => product?.product === loanApplication?.loanProduct
      )
      setCurrentProduct(
        selectedProduct?.product ?? productsToUse[0]?.product ?? null
      )
    }
  }, [capitalProducts, loanApplication, userHasRewards])
  if (
    isLoading ||
    !capitalProducts ||
    !mergedLoanProducts ||
    !userData ||
    currentProduct === null
  ) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Body className="mt-2 text-midnight-blue">Loading...</Body>
      </View>
    )
  }

  if (isError) {
    return <BFText>Error loading loan products</BFText>
  }

  const currentProductData = mergedLoanProducts.find(
    (p) => p.product === currentProduct
  )

  return (
    <View>
      {mergedLoanProducts?.length > 1 && (
        <View className="px-4 pb-4">
          <Tabs
            value={currentProduct ?? ""}
            onValueChange={(value) => {
              const index = mergedLoanProducts.findIndex(
                (p) => p.product === value
              )
              capitalProductsListScrolled({
                scrolledTo: index + 1,
                type: "click",
              })
              setCurrentProduct(value)
            }}
          >
            <TabsList className="flex-row w-full">
              {mergedLoanProducts?.map((lp) => {
                const productSlug = lp.product as CapitalProductSlug
                const title = getCapitalProductNameBySlug(productSlug) || "N/A"
                const isSelectable =
                  lp.isSelectable || lp.product === "standard_combined"

                return (
                  <TabsTrigger
                    key={lp.product}
                    value={lp.product ?? ""}
                    className={cn("flex-1", !isSelectable && "opacity-50")}
                  >
                    <BFText variant="H5">{title}</BFText>
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
        </View>
      )}

      <View className={"px-4 w-full"}>
        {/* If the product is 'standard_combined', render the combined card */}
        {currentProductData?.product === "standard_combined" ? (
          <LoanProductCardCombined
            key={currentProduct}
            loanId={loanId}
            cardData={currentProductData as CombinedProductType}
            loanData={loanApplication as LoanApplicationType}
          />
        ) : (
          <LoanProductCard
            key={currentProduct}
            cardIndex={mergedLoanProducts.findIndex(
              (p) => p.product === currentProduct
            )}
            loanId={loanId}
            cardData={currentProductData!}
            loanData={loanApplication}
            isSelected={
              currentProductData?.product === loanApplication?.loanProduct
            }
          />
        )}
      </View>
    </View>
  )
}

export default LoanProductScroller
